import { Field, FieldProps, FormikProps } from "formik";
import AppPanel from "@/features/invoices/order-confirmation-invoices/app-panel";
import { Box, Flex, FormControl, FormLabel, Grid, GridItem, Skeleton, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { toTitle, toUpper } from "@/utils/case";
import AppPriceInput from "@/features/invoices/app-price-input";
import AppCheckBox from "@/features/line-sheet-sets/app-checkbox";
import { ProformaInvoiceFormFields } from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";
import AppInput from "@/features/ui/app-input";
import AppCreditInvoiceSelector from "@/features/invoices/app-credit-invoice-selector";
import AppTagController from "@/features/invoices/app-tag-controller";
import { ListAllCreditInvoicesRequest } from "@/features/invoices/credit-invoices/hooks/use-list-all-credit-invoices";
import { AppUtils } from "@/features/ui/utils/app-utils";
import {
  formatCreditInvoiceLabel,
  isOtherBoutiqueCredit,
  ProformaCreditContext,
  UsedCreditInvoiceSummary,
} from "@/features/invoices/credit-invoices/credit-invoice.type";
import PriceUtils from "@/features/ui/utils/price-utils";
import { MathUtils } from "@/features/ui/utils/math-utils";
import AppButton from "@/features/line-sheet-sets/app-button";
import AddCreditInvoiceModal from "@/shared/add-credit-invoice-modal";
import useIdentity from "@/features/ui/hooks/use-identity";
import {
  confirmedAmountOf,
  isTotalRequiredAmountManual,
  proformaRequiredAmounts,
  resolveTotalRequiredAmount,
} from "@/features/invoices/proforma-invoices/cross-trade-fee";

interface ProformaInvoiceFigureFormControlModalProps<T> {
  formik: FormikProps<T>;
  isEditing?: boolean;
  isDisabled?: boolean;
  // 시즌 프로포마 — 디파짓(OC 잔여 디파짓에서 차감)을 보여준다
  isSeason?: boolean;
}

interface FormFields extends ProformaInvoiceFormFields {}

export default function ProformaInvoiceFigureFormControlModal<T extends FormFields>({
  formik,
  isEditing,
  isDisabled,
  isSeason = false,
}: ProformaInvoiceFigureFormControlModalProps<T>) {
  const { t, tTitle, tCurrencyString } = useI18nHelper();
  const [isAddCreditInvoiceModalOpen, setIsAddCreditInvoiceModalOpen] = useState(false);
  // 프로포마 금액 통화 — 폼에 없으면 유로
  const currency = formik.values.currency ?? "EUR";
  // 바이어 유로 송금이면 원화로 환산하지 않으므로 환율·은행 수수료를 안 쓰고, 송금 필요 금액도 유로 그대로다.
  const isBuyerToAgencyEur = !!formik.values.paidByAgency && !!formik.values.isBuyerToAgencyEur;

  const identity = useIdentity();

  // 총액 초기값 — 새 프로포마는 연결한 오더의 확정 금액 합으로 채워 두고, 담당자가 인보이스 금액으로 고친다.
  // (브랜드별 금액은 입력하지 않는다 — 서버가 총액을 브랜드별 확정금액 비율로 나눠 저장한다, 2026-10-30)
  const initialTotalAmount = confirmedAmountOf(formik.values.orderSheetSets ?? [], currency);
  useEffect(() => {
    if (
      formik.values.id === -1 &&
      formik.values.totalAmount?.value === undefined &&
      initialTotalAmount?.value !== undefined &&
      initialTotalAmount.value > 0
    ) {
      formik.setFieldValue("totalAmount", initialTotalAmount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.id, formik.values.totalAmount?.value, initialTotalAmount?.value]);

  const total = useMemo(() => {
    const amount = proformaRequiredAmounts(
      {
        totalAmount: formik.values.totalAmount,
        proformaInvoiceDetailList: formik.values.proformaInvoiceDetailList,
        usedCreditInvoices: formik.values.usedCreditInvoices,
        usedDeposit: formik.values.usedDeposit,
        crossTradeFeeRate: formik.values.crossTradeFeeRate,
      },
      currency
    ).amount;

    const usedCredit = PriceUtils.sum(
      formik.values.usedCreditInvoices.map((cDetail) => cDetail.amount),
      currency
    );

    const usedDeposit = formik.values.usedDeposit;

    // 송금 필요 금액 = 인보이스 총액 - 차감 크레딧 - 사용 디파짓
    const { requiredAmount } = proformaRequiredAmounts(
      {
        totalAmount: formik.values.totalAmount,
        proformaInvoiceDetailList: formik.values.proformaInvoiceDetailList,
        usedCreditInvoices: formik.values.usedCreditInvoices,
        usedDeposit,
        crossTradeFeeRate: formik.values.crossTradeFeeRate,
      },
      currency
    );

    return {
      amount,
      usedCredit,
      usedDeposit,
      requiredAmount,
    };
  }, [
    formik.values.totalAmount,
    formik.values.proformaInvoiceDetailList,
    formik.values.usedCreditInvoices,
    formik.values.usedDeposit,
    formik.values.crossTradeFeeRate,
    currency,
  ]);
  // 원화 환산 칸은 담당자가 직접 고친 송금 필요 금액이 있으면 그 값으로 환산한다
  const effectiveRequiredAmount = resolveTotalRequiredAmount(
    formik.values.totalRequiredAmount,
    total.requiredAmount
  );

  const handleCreditInvoiceChange = useCallback(
    (next: UsedCreditInvoiceSummary[]) => {
      formik.setValues((prev) => {
        return {
          ...prev,
          usedCreditInvoices: next,
        };
      });
    },
    [formik]
  );

  // 타부티크 크레딧: 바이어 소유인데 이 프로포르마의 부티크(원발행사 포함)도 에이전시도 아닌 곳이 발행한 크레딧.
  // 기본 선택지는 이 부티크 발행분만 보여주고, 체크하면 바이어의 다른 부티크 크레딧도 보여준다.
  const creditContext = useMemo(
    (): ProformaCreditContext => ({
      boutiqueIds: [
        formik.values.issuedBy?.id,
        formik.values.originallyIssuedBy?.id,
      ],
      agencyId: formik.values.issuedThrough?.id,
    }),
    [
      formik.values.issuedBy?.id,
      formik.values.originallyIssuedBy?.id,
      formik.values.issuedThrough?.id,
    ]
  );
  const hasOtherBoutiqueCredit = formik.values.usedCreditInvoices.some(
    (credit) => isOtherBoutiqueCredit(credit, creditContext)
  );
  const usesOtherBoutiqueCredit =
    !!formik.values.usesOtherBoutiqueCredit || hasOtherBoutiqueCredit;
  // 이미 고른 타부티크 크레딧이 있으면 끌 수 없다 — 먼저 그 크레딧을 빼야 한다.
  const otherBoutiqueCreditCheckBox = (
    <AppCheckBox
      size={"small"}
      name={tTitle("use_other_boutique_credit")}
      isChecked={usesOtherBoutiqueCredit}
      isDisabled={isDisabled || hasOtherBoutiqueCredit}
      onChange={(checked) => {
        formik.setFieldValue("usesOtherBoutiqueCredit", checked);
      }}
    />
  );

  if (!formik.values.id) {
    return (
      <AppPanel title={<Skeleton height={"32px"} />}>
        <Grid
          paddingRight={"12px"}
          gridTemplateColumns={"repeat(3, 1fr)"}
          gridTemplateRows={"20px 24px repeat(2, 20px 24px)"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Box />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton />
          <Skeleton />
          <Skeleton />

          <Skeleton gridColumn={"1/4"} />
        </Grid>
      </AppPanel>
    );
  }

  return (
    <>
    <AppPanel title={tTitle("common:invoice_figures")}>
      <Flex gap={"12px"} flexDirection={"column"}>
        {/* 시즌 — 연결한 OC의 잔여 디파짓에서 쓸 금액. 브랜드별로는 금액 비율대로 나눈다. */}
        {isSeason && (
          <Flex flexDirection={"column"} gap={"4px"}>
            <Box fontSize={"14px"} fontWeight={"700"}>
              {toUpper(t("deposit"))}
            </Box>
            <Grid
              gridTemplateColumns={"repeat(3, 1fr) 12px"}
              gridColumnGap={"8px"}
              gridRowGap={"4px"}
            >
              <FormControl
                display={"flex"}
                flexDirection={"column"}
                alignItems={"start"}
                gap={"4px"}
              >
                <FormLabel
                  margin={"0"}
                  padding={"0"}
                  minHeight={"0"}
                  minWidth={"0"}
                  fontSize={"12px"}
                  color={"#444440"}
                >
                  {toTitle(t("total_remaining"))}:
                </FormLabel>
                <AppPriceInput
                  value={formik.values.expectedRemainingDepositAmount}
                  isDisabled={isEditing}
                  isReadOnly={!isEditing}
                  showComma={true}
                />
              </FormControl>

              <Field name={"usedDeposit"}>
                {(props: FieldProps<FormFields["usedDeposit"], FormFields>) => {
                  return (
                    <FormControl
                      display={"flex"}
                      flexDirection={"column"}
                      alignItems={"start"}
                      gap={"4px"}
                    >
                      <FormLabel
                        margin={"0"}
                        padding={"0"}
                        minHeight={"0"}
                        minWidth={"0"}
                        fontSize={"12px"}
                        color={"#444440"}
                      >
                        {toTitle(t("total_using"))}:
                      </FormLabel>
                      <AppPriceInput
                        value={props.field.value}
                        currency={currency}
                        showComma={true}
                        onChange={(value) => {
                          props.form.setFieldValue(props.field.name, value);
                        }}
                        isDisabled={
                          isDisabled ||
                          (props.form.values.expectedRemainingDepositAmount
                            ?.value || 0) === 0
                        }
                        isReadOnly={!isEditing}
                      />
                    </FormControl>
                  );
                }}
              </Field>

              <Field name={"usedDepositComment"}>
                {(
                  props: FieldProps<FormFields["usedDepositComment"], FormFields>
                ) => {
                  return (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      justifyContent={"end"}
                    >
                      <AppInput
                        value={props.field.value}
                        placeholder={t("comment")}
                        isReadOnly={!isEditing}
                        onChange={(value) => {
                          props.form.setFieldValue(props.field.name, value);
                        }}
                      />
                    </Box>
                  );
                }}
              </Field>
            </Grid>
          </Flex>
        )}

        <Flex flexDirection={"column"} gap={"4px"}>
          <Flex alignItems={"center"} justifyContent={"space-between"}>
            <Box fontSize={"14px"} fontWeight={"700"}>{toUpper(t("credit"))}</Box>
            {isEditing && (
              <Flex alignItems={"center"} gap={"12px"}>
                {otherBoutiqueCreditCheckBox}
                <AppButton
                  onClick={() => setIsAddCreditInvoiceModalOpen(true)}
                  isDisabled={isDisabled}
                >
                  {toTitle(t("add_credit"))}
                </AppButton>
              </Flex>
            )}
          </Flex>
          <Grid
            gridTemplateColumns={"repeat(3, 1fr) 12px"}
            gridTemplateRows={"auto auto"}
            gridColumnGap={"8px"}
            gridRowGap={"8px"}
          >
            <Field name={"usedCreditInvoices"}>
              {(
                props: FieldProps<FormFields["usedCreditInvoices"], FormFields>
              ) => {
                const {
                  issuedBy,
                  orderedBy,
                  issuedThrough,
                  originallyIssuedBy,
                } = props.form.values;
                let request: ListAllCreditInvoicesRequest | undefined =
                  undefined;

                if (
                  issuedThrough &&
                  orderedBy &&
                  (issuedBy || originallyIssuedBy)
                ) {
                  request = {
                    isUsed__eq: false,
                    "issuedToCompany.id__eq": orderedBy.id,
                    // 기본: 부티크(원발행사 포함) 발행 크레딧 + 에이전시 발행 크레딧.
                    // 타부티크 크레딧 사용을 켜면 발행사 제한 없이 이 바이어의 크레딧을 모두 보여준다.
                    ...(usesOtherBoutiqueCredit
                      ? {}
                      : {
                          "issuedByCompany.id__in": AppUtils.filterDefined([
                            issuedBy?.id,
                            originallyIssuedBy?.id,
                            issuedThrough.id,
                          ]),
                        }),
                    "issuedThroughCompany.id__eq": issuedThrough.id,
                  };
                }

                return (
                  <FormControl
                    display={"flex"}
                    flexDirection={"column"}
                    alignItems={"start"}
                    gap={"4px"}
                  >
                    <FormLabel
                      margin={"0"}
                      padding={"0"}
                      minHeight={"0"}
                      minWidth={"0"}
                      fontSize={"12px"}
                      color={"#444440"}
                    >
                      {toTitle(t("using"))}:
                    </FormLabel>
                    <Box alignSelf={"stretch"}>
                      <AppCreditInvoiceSelector
                        width={"100%"}
                        excludeIds={formik.values.usedCreditInvoices.map(
                          (c) => c.id
                        )}
                        request={request}
                        isReadOnly={!isEditing}
                        isDisabled={isDisabled}
                        onSelect={(value) => {
                          // props.form.setValues((prev) => {
                          //   return {
                          //     ...prev,
                          //     usedCreditInvoices: [
                          //       ...prev.usedCreditInvoices,
                          //       value,
                          //     ],
                          //   };
                          // });
                          handleCreditInvoiceChange([
                            ...props.field.value,
                            value,
                          ]);
                        }}
                      />
                    </Box>
                  </FormControl>
                );
              }}
            </Field>

            <Flex
              display={"flex"}
              flexDirection={"column"}
              alignItems={"start"}
              justifyContent={"end"}
              gap={"4px"}
            >
              <Text
                margin={"0"}
                padding={"0"}
                minHeight={"0"}
                minWidth={"0"}
                fontSize={"12px"}
                color={"#444440"}
              >
                {toTitle(t("total_using"))}:
              </Text>
              <AppPriceInput
                isDisabled={isEditing}
                isReadOnly={!isEditing}
                showComma={true}
                value={PriceUtils.sum(
                  formik.values.usedCreditInvoices.map(
                    (creditInvoice) => creditInvoice.amount
                  ),
                  currency
                )}
              />
            </Flex>

            <Field name={"usedCreditComment"}>
              {(
                props: FieldProps<FormFields["usedCreditComment"], FormFields>
              ) => {
                return (
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    justifyContent={"end"}
                  >
                    <AppInput
                      value={props.field.value}
                      isReadOnly={isEditing}
                      placeholder={t("comment")}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                    />
                  </Box>
                );
              }}
            </Field>

            <GridItem gridColumn={"1/-2"} alignSelf={"stretch"}>
              <AppTagController
                values={formik.values.usedCreditInvoices.map((credit) => {
                  return {
                    id: credit.id,
                    name: formatCreditInvoiceLabel(credit, {
                      t,
                      formatAmount: tCurrencyString,
                    }),
                  };
                })}
                isDisabled={!isEditing}
                onChange={(tags) => {
                  handleCreditInvoiceChange(
                    formik.values.usedCreditInvoices.filter((credit) => {
                      return tags.some((tag) => tag.id === credit.id);
                    })
                  );
                }}
              />
            </GridItem>
          </Grid>
        </Flex>

        {/* 브랜드별 금액은 입력하지 않는다(서버가 총액을 연결 오더·OC 비율로 나눠 저장) — 브랜드만 보여 준다 */}
        {formik.values.proformaInvoiceDetailList.length > 0 && (
          <Text fontSize={"12px"} color={"#8A8A86"}>
            {toTitle(t("brand"))}:{" "}
            {Array.from(
              new Set(
                formik.values.proformaInvoiceDetailList
                  .map((pDetail) => pDetail.brand?.name)
                  .filter((name): name is string => !!name)
              )
            ).join(", ")}
          </Text>
        )}

        {/* 바이어 유로 송금 — 총 송금 필요 금액(원화) 칸 바로 위. 켜면 바이어 송금 요청을 유로로 만들고 환율 차액 정산을 하지 않는다. 우노 수수료는 원화 그대로. */}
        {formik.values.paidByAgency && identity?.company?.type !== "BOUTIQUE" && (
          <Grid gridTemplateColumns={"repeat(5, 1fr) 12px"} gridColumnGap={"8px"}>
            <GridItem gridColumn={"5"}>
              <Field name={"isBuyerToAgencyEur"}>
                {(props: FieldProps<FormFields["isBuyerToAgencyEur"], FormFields>) => {
                  return (
                    <AppCheckBox
                      size={"small"}
                      isChecked={!!props.field.value}
                      isDisabled={!isEditing || isDisabled}
                      name={"바이어 유로 송금 (차액 정산 없음)"}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                    />
                  );
                }}
              </Field>
            </GridItem>
          </Grid>
        )}

        <Grid
          gridTemplateColumns={"repeat(5, 1fr) 12px"}
          gridColumnGap={"8px"}
          gridRowGap={"4px"}
        >
          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:invoice_total_amount"))}:
            </Text>
            <Field name={"totalAmount"}>
              {(props: FieldProps<FormFields["totalAmount"], FormFields>) => {
                return (
                  <AppPriceInput
                    value={props.field.value ?? { currency }}
                    currency={currency}
                    showComma={true}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                    isDisabled={isDisabled}
                    isReadOnly={!isEditing}
                  />
                );
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("common:total_remittance_required_amount.abbr"))}:
            </Text>
            <Field name={"totalRequiredAmount"}>
              {(
                props: FieldProps<FormFields["totalRequiredAmount"], FormFields>
              ) => {
                const isManual = isTotalRequiredAmountManual(
                  props.field.value,
                  total.requiredAmount
                );
                return (
                  <Flex flexDirection={"column"} width={"100%"} gap={"2px"}>
                    <AppPriceInput
                      value={resolveTotalRequiredAmount(
                        props.field.value,
                        total.requiredAmount
                      )}
                      currency={currency}
                      showComma={true}
                      onChange={(value) => {
                        props.form.setFieldValue(props.field.name, value);
                      }}
                      isDisabled={isDisabled}
                      isReadOnly={!isEditing}
                    />
                    {/* 직접 고친 값이면 자동 계산값을 보여 주고 되돌릴 수 있게 한다 */}
                    {isManual && (
                      <Text fontSize={"11px"} color={"#8A8A86"}>
                        {t("total_remittance_required_amount.auto")}:{" "}
                        {tCurrencyString(total.requiredAmount.value || 0, {
                          type: "SYMBOL",
                          code: currency,
                        })}
                        {isEditing && (
                          <Text
                            as={"span"}
                            marginLeft={"6px"}
                            color={"#1272EF"}
                            cursor={"pointer"}
                            textDecoration={"underline"}
                            onClick={() => {
                              props.form.setFieldValue(props.field.name, undefined);
                            }}
                          >
                            {t("total_remittance_required_amount.reset")}
                          </Text>
                        )}
                      </Text>
                    )}
                  </Flex>
                );
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("estimated_exchange_fee"))}:
            </Text>
            <Field name={"exchangeRate"}>
              {(
                props: FieldProps<
                  FormFields["exchangeRate"],
                  FormFields
                >
              ) => {
                return <Flex flexDirection={"column"} width={"100%"}>
                  <AppPriceInput
                    isDisabled={isDisabled || isBuyerToAgencyEur}
                    showComma={true}
                    value={
                      props.field.value
                        ? {
                          value: props.field.value,
                          currency: "KRW",
                        }
                        : undefined
                    }
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value?.value);
                    }}
                  />
                  </Flex>
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(t("estimated_bank_fee"))}:
            </Text>
            <Field name={"bankFee"}>
              {(
                props: FieldProps<
                  FormFields["bankFee"],
                  FormFields
                >
              ) => {
                return <AppPriceInput
                  isDisabled={isDisabled || isBuyerToAgencyEur}
                  showComma={true}
                  value={props.field.value}
                  onChange={(value) => {
                    props.form.setFieldValue(props.field.name, value);
                  }}
                />
              }}
            </Field>
          </Flex>

          <Flex flexDirection={"column"} alignItems={"start"} gap={"4px"}>
            <Text
              margin={"0"}
              padding={"0"}
              minHeight={"0"}
              minWidth={"0"}
              fontSize={"12px"}
              color={"#444440"}
            >
              {toTitle(
                t(
                  isBuyerToAgencyEur
                    ? "common:total_remittance_required_amount_eur.abbr"
                    : "common:total_remittance_required_amount_krw.abbr"
                )
              )}:
            </Text>
            <AppPriceInput
              value={
                isBuyerToAgencyEur
                  ? effectiveRequiredAmount
                  : {
                      value: MathUtils.trim((effectiveRequiredAmount.value || 0) * (formik.values.exchangeRate || 1) + (formik.values.bankFee?.value || 0), -3, 'round'),
                      currency: "KRW",
                    }
              }
              isDisabled={isEditing}
              isReadOnly={!isEditing}
              showComma={true}
            />
          </Flex>

        </Grid>
      </Flex>
    </AppPanel>
    {isAddCreditInvoiceModalOpen && formik.values.orderedBy && (formik.values.issuedBy || formik.values.originallyIssuedBy) && formik.values.issuedThrough && (
      <AddCreditInvoiceModal
        isOpen={isAddCreditInvoiceModalOpen}
        onClose={() => setIsAddCreditInvoiceModalOpen(false)}
        orderedBy={formik.values.orderedBy}
        issuedBy={(formik.values.originallyIssuedBy || formik.values.issuedBy)!}
        issuedThrough={formik.values.issuedThrough}
      />
    )}
    </>
  );
}
